<template>
  <v-card flat class="pa-3 mt-4">
    <v-form class="d-flex flex-column" style="gap: 0.5rem">
      <!-- Clients -->
      <v-select
        v-if="!hideClientSelect"
        :label="$t('broker.labels.selectClient')"
        :items="clients"
        item-text="companyName"
        data-testid="quick-rfq-client"
        item-value="id"
        v-model="selectedClientId"
        @change="changeClientId"
        outlined
        dense
      />

      <div class="d-flex flex-wrap">
        <!-- Currency Pairs -->
        <v-col cols="12" md="6" class="pa-0">
          <v-select
            v-model="pairInput"
            :items="currencyPairs"
            data-testid="quick-rfq-pair"
            @input="$emit('pair', $event)"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            :label="$t('broker.labels.pair')"
            class="mr-2"
            outlined
            dense
          />
        </v-col>

        <!-- Amount -->
        <v-col cols="12" md="6" class="pa-0">
          <v-text-field
            :label="$t('broker.labels.amount')"
            v-model="amountInput"
            v-money="money"
            data-testid="quick-rfq-amount"
            @input="changeAmount"
            :key="resetRFQAmountKey"
            outlined
            dense
          />
        </v-col>
      </div>

      <div class="d-flex flex-column">
        <!-- Side -->
        <v-col class="pa-0 mt-1" cols="12" md="12" sm="12">
          <div>
            <div style="width: 100%; gap: 1rem;" class="class-here d-flex flex-wrap">
              <div>
                <span>{{ $t("quoteInfo.product") }}</span>
                <v-radio-group
                  row
                  v-model="product"
                  @change="changeProduct"
                  class="mt-0"
                  mandatory
                  :disabled="loadingNewRfq || gridActive || true"
                >
                  <v-radio class="my-2" value="SPOT">
                    <template v-slot:label>
                      <span style="font-size: 11px">
                        {{ $t("quoteInfo.spot").toUpperCase() }}
                      </span>
                    </template>
                  </v-radio>
                  <v-radio value="NDF">
                    <template v-slot:label>
                      <span style="font-size: 11px">
                        {{ $t("quoteInfo.ndf").toUpperCase() }}
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>

              <div>
                <span>{{ $t("quoteInfo.side") }}</span>
                <v-radio-group
                  v-model="side"
                  @change="changeSide"
                  class="mt-0"
                  row
                  mandatory
                  :disabled="loadingNewRfq || gridActive"
                >
                  <v-radio
                    class="my-2"
                    :label="$t('quoteInfo.buy').toUpperCase()"
                    value="BUY"
                  >
                  </v-radio>
                  <v-radio
                    :label="$t('quoteInfo.sell').toUpperCase()"
                    value="SELL"
                  >
                  </v-radio>
                </v-radio-group>
              </div>
            </div>
            <v-checkbox
              v-if="isMockDataOn"
              :disabled="false"
              v-model="scheduleEnabled"
              @change="toggleSchedule()"
            >
              <template v-slot:label>
                <span :class="{ 'font-weight-bold': scheduleEnabled }">
                  {{ $t("broker.labels.schedule") }}
                </span>
              </template>
            </v-checkbox>
          </div>
        </v-col>
        <!-- Spread -->
        <v-col cols="12" md="12" class="pa-0">
          <!-- <div>
                      <span>{{ $t('broker.labels.spread') }}</span>
                      <v-select
                        :items="[50, 100, 150, 200, 250, 300, 350, 400, 450, 500]"
                        v-model="selectedSpread"
                        outlined
                        dense
                      />
                    </div> -->
          <div v-if="product === 'NDF'">
            <span>{{ $t("quoteInfo.maturity") }}</span>
            <v-menu
              v-model="menuMaturity"
              :nudge-right="40"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="maturityDate"
                  prepend-icon="mdi-calendar"
                  readonly
                  dense
                  v-bind="attrs"
                  v-on="on"
                  style="width: 170px"
                  hide-details
                  class="mr-0 mr-sm-6 mb-0"
                >
                </v-text-field>
              </template>
              <v-date-picker
                color="primary"
                :locale="locale"
                @input="menuMaturity = false"
                @change="changeMaturityDate"
                no-title
                :min="firstDate"
                v-model="maturityDate"
              >
              </v-date-picker>
            </v-menu>
          </div>
        </v-col>
      </div>

      <!-- Settlement -->
      <!-- <div>
                  <span>{{ $t('quoteInfo.settlement') }}</span>
                  <v-radio-group
                    v-model="settlement"
                    class="mt-0"
                    mandatory
                    row
                    :disabled="loadingNewRfq || gridActive"
                  >
                    <v-radio
                      disabled
                      label="D+0"
                      value="d+0"
                      class="disabled"
                    >
                    </v-radio>
                    <v-radio
                      disabled
                      label="D+1"
                      value="d+1"
                      class="disabled"
                    >
                    </v-radio>
                    <v-radio
                      label="D+2"
                      value="d+2"
                    >
                    </v-radio>
                  </v-radio-group>
                </div> -->

      <!-- Request Quote Button -->
      <div class="d-flex justify-space-between align-center mb-0">
        <div class="d-flex flex-column">
          <v-menu
            v-if="scheduleEnabled"
            v-model="scheduleToMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="scheduleToInput"
                :label="$t('broker.labels.selectDate')"
                prepend-icon="mdi-calendar"
                readonly
                dense
                v-bind="attrs"
                v-on="on"
                style="width: 170px"
                hide-details
              >
              </v-text-field>
            </template>

            <v-date-picker
              @change="changeScheduleTo"
              v-model="scheduleToInput"
              no-title
              :min="minDate"
              @input="scheduleToMenu = false"
              color="primary"
            >
            </v-date-picker>
          </v-menu>
        </div>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import { VMoney } from "v-money";
import { useSettingsStore } from "../../store/index";
import { mapState } from "pinia";
export default {
  props: {
    loadingNewRfq: {
      required: true,
    },
    scheduleEnabled: {
      required: true,
    },
    minDate: {
      required: true,
    },
    clients: {
      required: true,
    },
    selectedPair: {
      required: true,
    },
    currencyPairs: {
      required: true,
    },
    gridActive: {
      required: true,
    },
    sideValue: {
      required: true,
    },
    productValue: {
      required: true,
    },
    maturityDateValue: {
      required: true,
    },
    amount: {
      required: true,
    },
    scheduleTo: {
      required: true,
    },
    resetRFQAmountKey: {
      required: true,
    },
    resetClient:{
      required:true
    },
    hideClientSelect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isoTomorrow = new Date(
      tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000
    )
      .toISOString()
      .substring(0, 10);
    return {
      product: this.productValue,
      side: this.sideValue,
      scheduleToMenu: undefined,
      maturityDate: this.maturityDateValue || isoTomorrow,
      amountInput: this.amount,
      scheduleToInput: this.scheduleTo,
      pairInput: this.selectedPair,
      selectedClientId: undefined,
      firstDate: isoTomorrow,
      menuMaturity: false,
    };
  },
  watch:{
    clients: {
      immediate: true,
      handler(newClients) {
        if (Array.isArray(newClients) && newClients.length === 1 && !this.selectedClientId) {
          this.selectedClientId = newClients[0].id
          this.changeClientId(this.selectedClientId)
        }
      }
    },
    sideValue(val) {
      this.side = val
    },
    productValue(val) {
      this.product = val
    },
    maturityDateValue(val) {
      this.maturityDate = val
    },
    amount(val) {
      this.amountInput = val
    },
    scheduleTo(val) {
      this.scheduleToInput = val
    },
    selectedPair(val) {
      this.pairInput = val
    },
    resetClient(val)
    {
      if(val === true)
      {
        this.selectedClientId = null
        this.$emit('reset', !val)
      }
    }
  },
  computed: {
    ...mapState(useSettingsStore, ["isMockDataOn"]),
    money() {
      let prefix = "";
      if (this.ccy === "USD") {
        prefix = "$ ";
      } else if (this.ccy === "EUR") {
        prefix = "€ ";
      }
      return {
        thousands: ",",
        prefix: prefix,
      };
    },
    locale() {
      const savedLocale = localStorage.getItem("userLanguage");
      return savedLocale || "en";
    },
  },
  methods: {
    toggleSchedule(checked) {
      if (checked) {
        this.scheduleToMenu = true;
      } else {
        this.scheduleToMenu = false;
        this.scheduleToInput = null;
        this.$emit("schedule-to", null);
      }
    },
    changeScheduleTo(v) {
      this.$emit("schedule-to", v);
    },
    changeMaturityDate(val) {
      this.$emit("maturity-change", val);
    },
    changeSide(val) {
      this.$emit("side", val);
    },
    changeProduct(val) {
      this.$emit("product", val);
    },
    changeAmount(val) {
      this.$emit("amount", val);
    },
    changeClientId(v) {
      this.$emit("handle-client-id", v);
    },
  },

  //TODO: emit product, side, scheduleTo
};
</script>
